import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { useMutation, useQueryClient, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { Link, useLocation, useSearch } from "wouter";
import { FmcsaIncidentWithRelations } from "@shared/schema";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit, Trash2, ExternalLink, FileText, Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CreateFmcsaIncidentModal } from "../components/modals/create-fmcsa-incident-modal";
import { EditFmcsaIncidentModal } from "../components/modals/edit-fmcsa-incident-modal";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { apiRequest } from "@/lib/queryClient";

interface ColumnConfig {
  id: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: 'incidentNumber', label: 'Incident #', defaultWidth: 120, minWidth: 80, maxWidth: 180 },
  { id: 'status', label: 'Status', defaultWidth: 100, minWidth: 70, maxWidth: 140 },
  { id: 'created', label: 'Created', defaultWidth: 120, minWidth: 90, maxWidth: 180 },
  { id: 'notes', label: 'Notes', defaultWidth: 200, minWidth: 120, maxWidth: 400 },
  { id: 'workOrders', label: 'Work Orders', defaultWidth: 150, minWidth: 100, maxWidth: 250 },
  { id: 'company', label: 'Company', defaultWidth: 180, minWidth: 120, maxWidth: 300 },
  { id: 'actions', label: '', defaultWidth: 80, minWidth: 60, maxWidth: 120 },
];

const STORAGE_KEY = 'fmcsa-incidents-column-widths';

function getStoredColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {}
}

export default function FmcsaIncidentsPage() {
  usePageTitle("FMCSA Incidents");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [location, setLocation] = useLocation();
  const searchString = useSearch();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<FmcsaIncidentWithRelations | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    return new URLSearchParams(window.location.search).get("status") || "unresolved";
  });
  const [expeditedFilter, setExpeditedFilter] = useState<"all" | "not_expedited" | "expedited">("all");

  useEffect(() => {
    const params = new URLSearchParams(searchString);
    const status = params.get("status");
    setStatusFilter(status || "unresolved");
  }, [searchString]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter !== 'unresolved') params.set('status', statusFilter);
    const newSearch = params.toString();
    if (newSearch !== searchString) {
      setLocation(`${location}${newSearch ? `?${newSearch}` : ''}`, { replace: true } as any);
    }
  }, [statusFilter, searchString, location, setLocation]);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredColumnWidths();
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = COLUMN_CONFIG.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(
        config.minWidth,
        Math.min(config.maxWidth, resizingRef.current.startWidth + diff)
      );
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnId]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => {
          saveColumnWidths(prev);
          return prev;
        });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColumnWidth = useCallback((columnId: string) => {
    return columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
  }, [columnWidths]);

  const {
    data: incidentsInfinite,
    isLoading,
    fetchNextPage: incidentsFetchNextPage,
    hasNextPage: incidentsHasNextPage,
    isFetchingNextPage: incidentsIsFetchingNextPage,
  } = useInfiniteQuery<{ data: FmcsaIncidentWithRelations[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ["/api/fmcsa-incidents", {
      search: debouncedSearch,
      status: statusFilter,
      expedited: expeditedFilter,
    }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      if (statusFilter !== 'all') qs.set('status', statusFilter);
      if (expeditedFilter === 'expedited') qs.set('expedited', 'true');
      if (expeditedFilter === 'not_expedited') qs.set('expedited', 'false');
      if (pageParam) qs.set('cursor', pageParam as string);
      const res = await fetch(`/api/fmcsa-incidents${qs.toString() ? `?${qs.toString()}` : ''}`, { credentials: 'include' });
      return res.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  const incidents = useMemo(() =>
    incidentsInfinite?.pages.flatMap(p => p.data) ?? [],
    [incidentsInfinite]
  );
  const incidentsTotalCount = incidentsInfinite?.pages[0]?.totalCount ?? incidents.length;

  // Server-side filtering handles status and expedited filters
  const filteredIncidents = incidents;

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/fmcsa-incidents/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/fmcsa-incidents"] });
      toast({
        title: "Success",
        description: "FMCSA incident deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete FMCSA incident",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (incident: FmcsaIncidentWithRelations) => {
    setSelectedIncident(incident);
    setEditModalOpen(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Resolved":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "Unresolved":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  const truncateText = (text: string | null, maxLength: number = 50) => {
    if (!text) return "No notes";
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (isLoading) {
    return (
      <ResponsiveLayout title="FMCSA Incidents" subtitle="Manage FMCSA incident inquiries and tracking">
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold tracking-tight">FMCSA Incidents</h1>
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-800 animate-pulse rounded-lg"></div>
            ))}
          </div>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout title="FMCSA Incidents" subtitle="Manage FMCSA incident inquiries and tracking">
      <div className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold tracking-tight">FMCSA Incidents</h1>
            <Button onClick={() => setCreateModalOpen(true)} data-testid="button-new-incident">
              <Plus className="h-4 w-4 mr-2" />
              New Incident
            </Button>
          </div>

          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by incident #, notes, company, or work order..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
                data-testid="input-search-incidents"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="button-clear-search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="select-status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="unresolved">Unresolved Only</SelectItem>
                <SelectItem value="resolved">Resolved Only</SelectItem>
                <SelectItem value="cancelled">Cancelled Only</SelectItem>
              </SelectContent>
            </Select>
            <Select value={expeditedFilter} onValueChange={(v) => setExpeditedFilter(v as typeof expeditedFilter)}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="select-expedited-filter">
                <SelectValue placeholder="Filter by expedited" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="not_expedited">Not Expedited</SelectItem>
                <SelectItem value="expedited">Expedited Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          {incidents && incidents.length > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {filteredIncidents.length} of {incidents.length} incidents
              {statusFilter !== "all" && ` (${statusFilter} only)`}
              {searchQuery && ` matching "${searchQuery}"`}
            </div>
          )}

          {!incidents?.length ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="text-center space-y-3">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No incidents found</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Get started by creating your first FMCSA incident.
                  </p>
                  <Button onClick={() => setCreateModalOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Incident
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : filteredIncidents.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="text-center space-y-3">
                  <Search className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No matching incidents</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Try adjusting your search or filter criteria.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                      setExpeditedFilter("all");
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table className="w-full" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      {COLUMN_CONFIG.map(col => (
                        <col key={col.id} style={{ width: `${getColumnWidth(col.id)}px` }} />
                      ))}
                    </colgroup>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="relative group" style={{ width: getColumnWidth('incidentNumber') }}>
                          <div className="pr-2">Incident #</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('incidentNumber', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('status') }}>
                          <div className="pr-2">Status</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('status', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('created') }}>
                          <div className="pr-2">Created</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('created', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('notes') }}>
                          <div className="pr-2">Notes</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('notes', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('workOrders') }}>
                          <div className="pr-2">Work Orders</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('workOrders', e)}
                          />
                        </TableHead>
                        <TableHead className="relative group" style={{ width: getColumnWidth('company') }}>
                          <div className="pr-2">Company</div>
                          <div 
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                            onMouseDown={(e) => handleResizeStart('company', e)}
                          />
                        </TableHead>
                        <TableHead style={{ width: getColumnWidth('actions') }}></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredIncidents.map((incident) => (
                        <TableRow key={incident.id} data-testid={`row-incident-${incident.id}`}>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <span className="font-semibold text-blue-600 dark:text-blue-400">
                                {incident.incidentNumber}
                              </span>
                              {incident.expedited && (
                                <Badge className="w-fit text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                  Expedited
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(incident.status)}>
                              {incident.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {incident.createdAt ? formatDistanceToNow(new Date(incident.createdAt), { addSuffix: true }) : "—"}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              {truncateText(incident.notes)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {incident.workOrders?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {incident.workOrders.slice(0, 2).map((wo) => (
                                  <Link
                                    key={wo.id}
                                    href={`/work-orders/${wo.id}`}
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-0.5"
                                  >
                                    {wo.workOrderNumber}
                                    <ExternalLink className="w-2.5 h-2.5" />
                                  </Link>
                                ))}
                                {incident.workOrders.length > 2 && (
                                  <span className="text-xs text-gray-500">
                                    +{incident.workOrders.length - 2} more
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {incident.company ? (
                              <Link 
                                href={`/companies/${incident.company.id}`}
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
                              >
                                {incident.company.name}
                                <ExternalLink className="w-3 h-3" />
                              </Link>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(incident)}
                                data-testid={`button-edit-${incident.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`button-delete-${incident.id}`}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Incident</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete incident {incident.incidentNumber}? 
                                      This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(incident.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <CreateFmcsaIncidentModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Showing {incidents.length} of {incidentsTotalCount}
        </p>
        {incidentsHasNextPage && (
          <Button variant="outline" onClick={() => incidentsFetchNextPage()} disabled={incidentsIsFetchingNextPage}
            aria-label="Load more incidents" aria-busy={incidentsIsFetchingNextPage}>
            {incidentsIsFetchingNextPage ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
            ) : "Load more"}
          </Button>
        )}
      </div>

      {selectedIncident && (
        <EditFmcsaIncidentModal
          open={editModalOpen}
          onOpenChange={(open: boolean) => {
            setEditModalOpen(open);
            if (!open) setSelectedIncident(null);
          }}
          incident={selectedIncident}
        />
      )}
    </ResponsiveLayout>
  );
}
